import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Database, Plus, X, Key, Zap, Search, Settings, Eye, EyeOff, Copy } from 'lucide-react'
import { toast } from 'sonner'
import type { ClientCRMForm } from '@/types/client'

interface ClientCRMStepProps {
  data: ClientCRMForm
  onChange: (data: Partial<ClientCRMForm>) => void
  isLoading?: boolean
}

const CRM_SYSTEM_OPTIONS = [
  'HubSpot',
  'Salesforce',
  'Pipedrive',
  'Zoho CRM',
  'Monday.com',
  'Airtable',
  'Custom',
  'Other'
]

const SEQUENCER_SYSTEM_OPTIONS = [
  'Smartlead',
  'Apollo',
  'Outreach',
  'SalesLoft',
  'Mixmax',
  'Reply.io',
  'Custom',
  'Other'
]

const ENRICHMENT_TOOL_OPTIONS = [
  'Apollo',
  'ZoomInfo',
  'Clearbit',
  'Hunter.io',
  'Skrapp',
  'PhantomBuster',
  'LinkedIn Sales Navigator',
  'Custom',
  'Other'
]

export function ClientCRMStep({ data, onChange, isLoading }: ClientCRMStepProps) {
  const [newEnrichmentTool, setNewEnrichmentTool] = useState('')
  const [showCredentials, setShowCredentials] = useState({
    crm: false,
    sequencer: false,
    enrichment: {} as Record<string, boolean>
  })
  const [passwordVisibility, setPasswordVisibility] = useState<{
    crm: Record<string, boolean>
    sequencer: Record<string, boolean>
    enrichment: Record<string, Record<string, boolean>>
  }>({
    crm: {},
    sequencer: {},
    enrichment: {}
  })

  const togglePasswordVisibility = (
    section: 'crm' | 'sequencer' | 'enrichment',
    field: string,
    toolName?: string
  ) => {
    setPasswordVisibility(prev => {
      if (section === 'enrichment' && toolName) {
        return {
          ...prev,
          enrichment: {
            ...prev.enrichment,
            [toolName]: {
              ...(prev.enrichment[toolName] || {}),
              [field]: !(prev.enrichment[toolName]?.[field])
            }
          }
        }
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: !prev[section][field]
        }
      }
    })
  }

  const handleCopyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copied to clipboard!`)
    } catch (error) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleAddEnrichmentTool = () => {
    if (!newEnrichmentTool.trim()) {
      toast.error('Please enter a tool name')
      return
    }
    
    const updatedTools = [...(data.enrichment_tools || []), newEnrichmentTool.trim()]
    onChange({ enrichment_tools: updatedTools })
    setNewEnrichmentTool('')
    toast.success('Tool added successfully')
  }

  const handleRemoveEnrichmentTool = (index: number) => {
    const toolToRemove = data.enrichment_tools?.[index]
    const updatedTools = data.enrichment_tools?.filter((_, i) => i !== index) || []
    
    // Remove credentials for this tool
    if (toolToRemove && data.enrichment_credentials?.[toolToRemove]) {
      const updatedCredentials = { ...data.enrichment_credentials }
      delete updatedCredentials[toolToRemove]
      onChange({ 
        enrichment_tools: updatedTools,
        enrichment_credentials: updatedCredentials
      })
    } else {
      onChange({ enrichment_tools: updatedTools })
    }
    
    toast.success('Tool removed')
  }

  const handleCredentialChange = (type: 'crm' | 'sequencer' | 'enrichment', key: string, value: string, toolName?: string) => {
    if (type === 'enrichment' && toolName) {
      // Store credentials per tool
      const currentCredentials = data.enrichment_credentials || {}
      const toolCredentials = currentCredentials[toolName] || {}
      const updatedCredentials = {
        ...currentCredentials,
        [toolName]: {
          ...toolCredentials,
          [key]: value
        }
      }
      onChange({ enrichment_credentials: updatedCredentials })
    } else {
      const currentCredentials = data[`${type}_credentials`] || {}
      const updatedCredentials = {
        ...currentCredentials,
        [key]: value
      }
      onChange({ [`${type}_credentials`]: updatedCredentials })
    }
  }

  const handleRemoveCredential = (type: 'crm' | 'sequencer' | 'enrichment', key: string) => {
    const currentCredentials = data[`${type}_credentials`] || {}
    const updatedCredentials = { ...currentCredentials }
    delete updatedCredentials[key]
    onChange({ [`${type}_credentials`]: updatedCredentials })
  }

  return (
    <div className="space-y-6">
      {/* CRM System */}
      <Card>
        <CardHeader>
          <CardTitle>CRM System</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">CRM System</Label>
            <select
              value={data.crm_system || ''}
              onChange={(e) => onChange({ crm_system: e.target.value })}
              className="w-full p-2 border border-input rounded-md bg-background"
            >
              <option value="">Select CRM system...</option>
              {CRM_SYSTEM_OPTIONS.map(system => (
                <option key={system} value={system}>{system}</option>
              ))}
            </select>
          </div>

          {/* CRM Credentials */}
          {data.crm_system && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">CRM Credentials</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCredentials(prev => ({ ...prev, crm: !prev.crm }))}
                >
                  <Key className="h-4 w-4 mr-2" />
                  {showCredentials.crm ? 'Hide' : 'Show'} Credentials
                </Button>
              </div>

              {showCredentials.crm && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">API Key / Token</Label>
                      <div className="relative">
                      <Input
                        placeholder="Enter API key..."
                        value={data.crm_credentials?.api_key || ''}
                        onChange={(e) => handleCredentialChange('crm', 'api_key', e.target.value)}
                          type={passwordVisibility.crm.api_key ? 'text' : 'password'}
                          className="pr-20"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => togglePasswordVisibility('crm', 'api_key')}
                          >
                            {passwordVisibility.crm.api_key ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          {data.crm_credentials?.api_key && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleCopyToClipboard(
                                data.crm_credentials.api_key || '',
                                'API Key'
                              )}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Base URL</Label>
                      <Input
                        placeholder="https://api.example.com"
                        value={data.crm_credentials?.base_url || ''}
                        onChange={(e) => handleCredentialChange('crm', 'base_url', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Username / Email</Label>
                      <Input
                        placeholder="Enter username..."
                        value={data.crm_credentials?.username || ''}
                        onChange={(e) => handleCredentialChange('crm', 'username', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Password</Label>
                      <div className="relative">
                      <Input
                        placeholder="Enter password..."
                        value={data.crm_credentials?.password || ''}
                        onChange={(e) => handleCredentialChange('crm', 'password', e.target.value)}
                          type={passwordVisibility.crm.password ? 'text' : 'password'}
                          className="pr-20"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => togglePasswordVisibility('crm', 'password')}
                          >
                            {passwordVisibility.crm.password ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          {data.crm_credentials?.password && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleCopyToClipboard(
                                data.crm_credentials.password || '',
                                'Password'
                              )}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sequencer System */}
      <Card>
        <CardHeader>
          <CardTitle>Sequencer System</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Sequencer System</Label>
            <select
              value={data.sequencer_system || ''}
              onChange={(e) => onChange({ sequencer_system: e.target.value })}
              className="w-full p-2 border border-input rounded-md bg-background"
            >
              <option value="">Select sequencer system...</option>
              {SEQUENCER_SYSTEM_OPTIONS.map(system => (
                <option key={system} value={system}>{system}</option>
              ))}
            </select>
          </div>

          {/* Sequencer Credentials */}
          {data.sequencer_system && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Sequencer Credentials</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCredentials(prev => ({ ...prev, sequencer: !prev.sequencer }))}
                >
                  <Key className="h-4 w-4 mr-2" />
                  {showCredentials.sequencer ? 'Hide' : 'Show'} Credentials
                </Button>
              </div>

              {showCredentials.sequencer && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">API Key / Token</Label>
                      <div className="relative">
                      <Input
                        placeholder="Enter API key..."
                        value={data.sequencer_credentials?.api_key || ''}
                        onChange={(e) => handleCredentialChange('sequencer', 'api_key', e.target.value)}
                          type={passwordVisibility.sequencer.api_key ? 'text' : 'password'}
                          className="pr-20"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => togglePasswordVisibility('sequencer', 'api_key')}
                          >
                            {passwordVisibility.sequencer.api_key ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          {data.sequencer_credentials?.api_key && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleCopyToClipboard(
                                data.sequencer_credentials.api_key || '',
                                'API Key'
                              )}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Client ID</Label>
                      <Input
                        placeholder="Enter client ID..."
                        value={data.sequencer_credentials?.client_id || ''}
                        onChange={(e) => handleCredentialChange('sequencer', 'client_id', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Base URL</Label>
                      <Input
                        placeholder="https://api.example.com"
                        value={data.sequencer_credentials?.base_url || ''}
                        onChange={(e) => handleCredentialChange('sequencer', 'base_url', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Username / Email</Label>
                      <Input
                        placeholder="Enter username..."
                        value={data.sequencer_credentials?.username || ''}
                        onChange={(e) => handleCredentialChange('sequencer', 'username', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Password</Label>
                      <div className="relative">
                      <Input
                        placeholder="Enter password..."
                        value={data.sequencer_credentials?.password || ''}
                        onChange={(e) => handleCredentialChange('sequencer', 'password', e.target.value)}
                          type={passwordVisibility.sequencer.password ? 'text' : 'password'}
                          className="pr-20"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => togglePasswordVisibility('sequencer', 'password')}
                          >
                            {passwordVisibility.sequencer.password ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          {data.sequencer_credentials?.password && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleCopyToClipboard(
                                data.sequencer_credentials.password || '',
                                'Password'
                              )}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Other Tools */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-primary" />
            <CardTitle>Other Tools</CardTitle>
          </div>
          <CardDescription>
            Additional tools and integrations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Add Tool</Label>
            <div className="flex space-x-2">
              <Input
                placeholder="Enter tool name..."
                value={newEnrichmentTool}
                onChange={(e) => setNewEnrichmentTool(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddEnrichmentTool()
                  }
                }}
                className="flex-1"
              />
              <Button onClick={handleAddEnrichmentTool} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Tools with Individual Credential Sections */}
          {data.enrichment_tools && data.enrichment_tools.length > 0 && (
            <div className="space-y-4">
              <Label className="text-sm font-medium">Active Tools</Label>
              
              {data.enrichment_tools.map((tool, index) => {
                const toolCredentials = data.enrichment_credentials?.[tool] || {}
                const isExpanded = showCredentials.enrichment[tool] || false
                
                return (
                  <Card key={index} className="border">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="h-2 w-2 bg-primary rounded-full"></div>
                          <CardTitle className="text-base">{tool}</CardTitle>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCredentials(prev => ({
                              ...prev,
                              enrichment: {
                                ...prev.enrichment,
                                [tool]: !isExpanded
                              }
                            }))}
                          >
                            <Key className="h-4 w-4 mr-2" />
                            {isExpanded ? 'Hide' : 'Configure'} Credentials
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveEnrichmentTool(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-muted-foreground">API Key / Token</Label>
                              <div className="relative">
                              <Input
                                placeholder="Enter API key..."
                                value={toolCredentials.api_key || ''}
                                onChange={(e) => handleCredentialChange('enrichment', 'api_key', e.target.value, tool)}
                                  type={passwordVisibility.enrichment[tool]?.api_key ? 'text' : 'password'}
                                  className="pr-20"
                                />
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => togglePasswordVisibility('enrichment', 'api_key', tool)}
                                  >
                                    {passwordVisibility.enrichment[tool]?.api_key ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </Button>
                                  {toolCredentials.api_key && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => handleCopyToClipboard(
                                        toolCredentials.api_key || '',
                                        'API Key'
                                      )}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Client ID</Label>
                              <Input
                                placeholder="Enter client ID..."
                                value={toolCredentials.client_id || ''}
                                onChange={(e) => handleCredentialChange('enrichment', 'client_id', e.target.value, tool)}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-muted-foreground">Base URL</Label>
                              <Input
                                placeholder="https://api.example.com"
                                value={toolCredentials.base_url || ''}
                                onChange={(e) => handleCredentialChange('enrichment', 'base_url', e.target.value, tool)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Username / Email</Label>
                              <Input
                                placeholder="Enter username..."
                                value={toolCredentials.username || ''}
                                onChange={(e) => handleCredentialChange('enrichment', 'username', e.target.value, tool)}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Password</Label>
                            <div className="relative">
                            <Input
                              placeholder="Enter password..."
                              value={toolCredentials.password || ''}
                              onChange={(e) => handleCredentialChange('enrichment', 'password', e.target.value, tool)}
                                type={passwordVisibility.enrichment[tool]?.password ? 'text' : 'password'}
                                className="pr-20"
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => togglePasswordVisibility('enrichment', 'password', tool)}
                                >
                                  {passwordVisibility.enrichment[tool]?.password ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                                {toolCredentials.password && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => handleCopyToClipboard(
                                      toolCredentials.password || '',
                                      'Password'
                                    )}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tech Stack Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-primary" />
            <CardTitle>Tech Stack Summary</CardTitle>
        </div>
        <CardDescription>
            Overview of configured systems and integrations
        </CardDescription>
      </CardHeader>
      <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {data.crm_system ? '✓' : '○'}
              </div>
              <div className="text-sm text-muted-foreground">CRM System</div>
              {data.crm_system && (
                <div className="text-xs text-muted-foreground mt-1">{data.crm_system}</div>
              )}
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {data.sequencer_system ? '✓' : '○'}
              </div>
              <div className="text-sm text-muted-foreground">Sequencer</div>
              {data.sequencer_system && (
                <div className="text-xs text-muted-foreground mt-1">{data.sequencer_system}</div>
              )}
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {data.enrichment_tools && data.enrichment_tools.length > 0 ? data.enrichment_tools.length : '○'}
              </div>
              <div className="text-sm text-muted-foreground">Other Tools</div>
              {data.enrichment_tools && data.enrichment_tools.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  {data.enrichment_tools.length} tool{data.enrichment_tools.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
      </CardContent>
    </Card>
    </div>
  )
}

